import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

interface ShortcutsOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ShortcutRow {
  keys: string;
  label: string;
}

interface ShortcutGroup {
  title: string;
  rows: ShortcutRow[];
}

const groups: ShortcutGroup[] = [
  {
    title: 'Tools',
    rows: [
      { keys: 'V', label: 'Select' },
      { keys: 'P', label: 'Place' },
      { keys: 'M', label: 'Move' },
      { keys: 'R', label: 'Rotate' },
      { keys: 'C', label: 'Scale (drag from object center)' },
      { keys: 'W', label: 'Draw wall' },
      { keys: 'L', label: 'Measure' },
    ],
  },
  {
    title: 'Editing',
    rows: [
      { keys: 'Ctrl/Cmd + Z', label: 'Undo' },
      { keys: 'Ctrl/Cmd + Shift + Z', label: 'Redo' },
      { keys: 'Ctrl/Cmd + D', label: 'Duplicate selection' },
      { keys: 'Ctrl/Cmd + G', label: 'Group selection (Shift = ungroup)' },
      { keys: 'Alt + click', label: 'Select one object out of a group' },
      { keys: 'Ctrl/Cmd + S', label: 'Save (server or JSON)' },
      { keys: 'Delete / Backspace', label: 'Delete selection' },
      { keys: '↑ ↓ ← →', label: 'Nudge (0.1 m, or grid size with snap on)' },
      { keys: 'Esc', label: 'Deselect / cancel wall / exit placement' },
      { keys: 'Enter', label: 'Finish wall (wall-draw tool)' },
    ],
  },
  {
    title: 'View',
    rows: [
      { keys: 'G', label: 'Toggle grid' },
      { keys: '`', label: 'Toggle snap-to-grid' },
      { keys: 'F', label: 'Toggle walkthrough (3D only)' },
      { keys: 'E', label: '3D edit mode — freeze camera, drag/place/delete (3D only)' },
      { keys: 'Mouse wheel', label: 'Zoom' },
      { keys: 'Middle-click drag', label: 'Pan' },
      { keys: '?', label: 'Show this overlay' },
    ],
  },
];

export const ShortcutsOverlay: React.FC<ShortcutsOverlayProps> = ({ open, onOpenChange }) => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
      <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-xl translate-x-[-50%] translate-y-[-50%] bg-white dark:bg-gray-900 rounded-lg shadow-lg p-0 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 p-5">
          <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Keyboard shortcuts
          </Dialog.Title>
          <Dialog.Close className="inline-flex items-center justify-center rounded-md p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="h-5 w-5" />
          </Dialog.Close>
        </div>

        <div className="p-5 space-y-6">
          {groups.map((group) => (
            <section key={group.title}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                {group.title}
              </h3>
              <table className="w-full text-sm">
                <tbody>
                  {group.rows.map((row) => (
                    <tr key={row.keys} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                      <td className="py-1.5 pr-4 w-44">
                        <kbd className="inline-block px-2 py-0.5 text-xs font-mono bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded">
                          {row.keys}
                        </kbd>
                      </td>
                      <td className="py-1.5 text-gray-700 dark:text-gray-300">{row.label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);

export default ShortcutsOverlay;
